/**
 * Where the open map is stored: online (on the server, synced to the user's
 * devices, shareable) or on this device only. Shown next to the map's title
 * (#map-storage-btn); the connection to the server is a separate indicator
 * (engine/core/server-status.js).
 *
 * Also the one place that moves a map online, used by that button, the vault
 * and the Sharing section of a map on this device; a move that stopped (tab
 * closed, connection lost) is offered again after signing in. The same for
 * moving an online map to this device (desktop app; the vault and Document
 * Properties). A new online map created while the server could not be
 * reached was kept on this device (VaultStorage.createDoc, `goesOnline`):
 * it is moved online as soon as the server can be reached, with a note.
 */
import { VaultStorage, isOnlineSlotEnabled } from "../../../engine/crypto/vault-storage.js";
import { storageOf } from "../../../engine/core/vault-folders.js";
import { on, Events } from "../../../engine/core/events.js";
import * as Store from "../store/index.js";
import { confirmAction, vaultDialog } from "./vault/dialogs.js";
import { withOperation } from "./operation-progress.js";
import { listStoredAttachments } from "../../../engine/storage/index.js";
import { knownClearRisk, storageMayBeCleared } from "../../../engine/services/device-storage.js";
import { showToast } from "./toast.js";
import { canMoveToDevice, describeMoveToDevice, moveToDevice, unfinishedMovesToDevice, cancelMoveToDevice, movesToDeviceDecidable } from "../../../engine/services/move-to-device.js";
import { isKnownOnline, knownTitle, stillOnline } from "../../../engine/sync/known-online.js";
import { getConnectionState as vaultConnection } from "../../../engine/sync/user-crdt-bridge.js";
import { escapeHtml } from "../../../engine/core/validation.js";
import { t } from "../../../shared/i18n/index.js";

const CLOUD = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/></svg>';
const DEVICE = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="12" rx="2"/><path d="M2 20h20"/></svg>';

let deps = {
  getCurrentUser: () => null,
  reopenDoc: null,
  openProperties: null,
  openLogin: null,
  closeMap: null,
};

/** The vault entry of a map, or null. */
export function mapEntry(docId) {
  return (docId && VaultStorage.getIndex?.()?.docs?.find((d) => d.docId === docId)) || null;
}

/** 'online', 'local', or null when the map has no vault entry (yet). */
export function mapStorageOf(docId) {
  const entry = mapEntry(docId);
  return entry ? storageOf(entry) : null;
}

/** Tauri with no server configured: everything lives on this device. */
function localOnlyMode() {
  return document.body.dataset.mode === "local-only";
}

function canMoveOnline() {
  return !!deps.getCurrentUser?.() && isOnlineSlotEnabled();
}

/**
 * Move a map from this device to the online vault, after confirmation. The
 * open map is reopened online so its content goes to the server.
 * @returns {Promise<boolean>} true once the map is online
 */
export async function moveMapOnline(docId) {
  const entry = mapEntry(docId);
  if (!entry || storageOf(entry) !== "local") return false;
  if (!canMoveOnline()) {
    showToast(t("app.mapStorage.signInFirst"));
    return false;
  }
  const title = entry.title || t("common.untitled");
  // Its attachments on this device move with it: say how many.
  let files = 0;
  try { files = (await listStoredAttachments("device", docId)).length; } catch { /* none readable */ }
  const message = files
    ? `${t("app.mapStorage.moveOnlineConfirm", { title })} ${t("app.mapStorage.moveOnlineFiles", { count: files })}`
    : t("app.mapStorage.moveOnlineConfirm", { title });
  const confirmed = await confirmAction(message, {
    title: t("app.mapStorage.moveOnlineTitle"),
    submit: t("vault.map.moveOnline"),
  });
  if (!confirmed) return false;
  return runMove(docId, title);
}

/**
 * Move it, with a progress card that also reports a failure.
 * @param {{done?: string, after?: string}} [said] the note once online, and
 *   after a failure (default: moved online; moving again continues)
 */
async function runMove(docId, title, said = {}) {
  startedHere(docId);
  let result;
  try {
    result = await withOperation(t("app.mapStorage.moving", { title }),
      (progress) => VaultStorage.promoteToOnline(docId, { report: progress.update }),
      (outcome) => outcome.ok
        ? [said.done || t("app.mapStorage.movedOnline", { title })]
        : [`${t("vault.promote.failed", { error: outcome.error })} ${said.after || t("app.mapStorage.moveResumes")}`, true]);
  } catch {
    return false;
  }
  if (!result.ok) return false;
  if (Store.get("currentDocId") === docId) await deps.reopenDoc?.(docId);
  renderMapStorage();
  return true;
}

/**
 * Move an online map to this device (desktop app), after a confirmation that
 * says what goes with it and what is deleted.
 * @returns {Promise<boolean>} true once the map is on this device
 */
export async function moveMapToDevice(docId) {
  if (!canMoveToDevice(docId)) return false;
  const title = mapEntry(docId)?.title || t("common.untitled");
  let about;
  try {
    about = await describeMoveToDevice(docId);
  } catch {
    showToast(t("app.mapStorage.toDeviceNeedsServer"));
    return false;
  }
  const said = [t("app.mapStorage.moveToDeviceConfirm", { title })];
  if (about.files) said.push(t("app.mapStorage.moveToDeviceFiles", { count: about.files }));
  if (about.members) said.push(t("app.mapStorage.moveToDeviceMembers", { count: about.members }));
  if (about.links) said.push(t("app.mapStorage.moveToDeviceLinks"));
  const confirmed = await vaultDialog({
    title: t("app.mapStorage.moveToDeviceTitle"),
    submit: t("vault.map.moveToDevice"),
    danger: true,
    content: said.map((text) => `<p class="vault-dialog-message">${escapeHtml(text)}</p>`).join(""),
    onSubmit: () => true,
  });
  if (!confirmed) return false;
  return runMoveToDevice(docId, title);
}

/** The open map reopens where it now is. */
async function reopenIfOpen(docId) {
  if (Store.get("currentDocId") === docId) await deps.reopenDoc?.(docId);
}

async function runMoveToDevice(docId, title) {
  startedHere(docId);
  let result;
  try {
    result = await withOperation(t("app.mapStorage.movingToDevice", { title }),
      (progress) => moveToDevice(docId, { report: progress.update, reopen: reopenIfOpen }),
      (outcome) => outcome.ok
        ? [t("app.mapStorage.movedToDevice", { title })]
        : [`${t("app.mapStorage.moveToDeviceFailed", { error: outcome.error })} ${t(unfinishedMovesToDevice().some((move) => move.docId === docId && move.phase === "finish")
          ? "app.mapStorage.moveToDeviceFinishesLater" : "app.mapStorage.moveToDeviceStays")}`, true]);
  } catch {
    return false;
  }
  renderMapStorage();
  return !!result?.ok;
}

// "user/docId" of the unfinished moves offered in this session, and of the
// moves started in it (a failure says how to continue: not offered again
// before the next start).
const offered = new Set();
function startedHere(docId) {
  const user = deps.getCurrentUser?.()?.user_id;
  if (user) offered.add(`${user}/${docId}`);
}

/**
 * After signing in (or at start): moves that stopped before the end are
 * offered again (continuing uses the same online key and skips files
 * already sent). One that switched but stopped before tidying up is
 * finished silently. Called on each signal that may make it possible:
 * signed in, the account's vault synced, its index read. Each move is
 * handled once, as soon as its map is listed (and, for a move to this
 * device, once the device's maps are loaded too: at start they may come
 * after the account's).
 */
async function offerUnfinishedMoves() {
  const user = deps.getCurrentUser?.();
  if (!user || !canMoveOnline() || vaultConnection() !== "synced") return;
  if (!VaultStorage.getIndex?.()?.keypair?.publicKey) return;
  await moveMapsCreatedOffline();
  const key = (move) => `${user.user_id}/${move.docId}`;
  const fresh = (move) => !!mapEntry(move.docId) && !offered.has(key(move));
  // A map created offline is not asked about: it goes online by itself.
  const moves = (VaultStorage.unfinishedMovesOnline?.() || []).filter(fresh)
    .filter((move) => !(mapStorageOf(move.docId) === "local" && mapEntry(move.docId)?.goesOnline));
  // Not before the device's maps are loaded too: until then an online map
  // reads as not movable here, and its move would be cancelled.
  const toDevice = movesToDeviceDecidable() ? unfinishedMovesToDevice().filter(fresh) : [];
  for (const move of [...moves, ...toDevice]) offered.add(key(move));
  for (const move of toDevice) {
    if (move.phase === "finish") {
      await moveToDevice(move.docId, { reopen: reopenIfOpen }).catch(() => {});
      renderMapStorage();
      continue;
    }
    if (!canMoveToDevice(move.docId)) {
      await cancelMoveToDevice(move.docId);
      continue;
    }
    const title = mapEntry(move.docId)?.title || move.title || t("common.untitled");
    const again = await confirmAction(t("app.mapStorage.resumeToDeviceMessage", { title }), {
      title: t("app.mapStorage.resumeToDeviceTitle"),
      submit: t("app.mapStorage.resumeSubmit"),
      cancel: t("app.mapStorage.resumeToDeviceCancel"),
    });
    if (again) await runMoveToDevice(move.docId, title);
    else await cancelMoveToDevice(move.docId);
  }
  for (const move of moves) {
    const storage = mapStorageOf(move.docId);
    if (storage === "online") {
      await VaultStorage.promoteToOnline(move.docId).catch(() => {});
    } else if (storage === "local") {
      const title = mapEntry(move.docId)?.title || move.title || t("common.untitled");
      const again = await confirmAction(t("app.mapStorage.resumeMessage", { title }), {
        title: t("app.mapStorage.resumeTitle"),
        submit: t("app.mapStorage.resumeSubmit"),
        cancel: t("common.later"),
      });
      if (again) await runMove(move.docId, title);
    }
  }
}

// Maps created offline tried in this connection to the server, and those
// being moved now. A new connection tries again.
const triedOnline = new Set();
const goingOnline = new Set();

/** Maps created while the server could not be reached go online now. */
async function moveMapsCreatedOffline() {
  if (globalThis.navigator?.onLine === false) return;
  const maps = (VaultStorage.getIndex?.()?.docs || [])
    .filter((doc) => storageOf(doc) === "local" && doc.goesOnline && !triedOnline.has(doc.docId) && !goingOnline.has(doc.docId));
  for (const doc of maps) {
    triedOnline.add(doc.docId);
    goingOnline.add(doc.docId);
    const title = doc.title || t("common.untitled");
    try {
      await runMove(doc.docId, title, {
        done: t("app.mapStorage.wentOnline", { title }),
        after: t("app.mapStorage.goesOnlineLater"),
      });
    } finally {
      goingOnline.delete(doc.docId);
    }
  }
}

// ---- Maps no longer online (engine/sync/known-online.js) ----

const goneAsked = new Set();

/**
 * Maps this device knew were online that the server no longer has: moved to
 * another device, or deleted there. One kept on this device (moved here)
 * just loses its stale online entry. For the others the user is told, may
 * save a backup file of this device's copy (changes never sent exist only
 * in it), and the copy is removed; "Later" asks again next time.
 */
async function handleGoneMaps(docIds) {
  const pending = docIds.filter((docId) => !goneAsked.has(docId));
  if (!pending.length) return;
  pending.forEach((docId) => goneAsked.add(docId));
  const keptHere = (docId) => (VaultStorage.getIndex?.()?.docs || []).some((doc) => doc.docId === docId && storageOf(doc) === "local");
  const gone = [];
  for (const docId of pending) {
    if (!keptHere(docId)) { gone.push(docId); continue; }
    await VaultStorage.forgetGoneMap(docId).catch(() => {});
    if (Store.get("currentDocId") === docId) await deps.reopenDoc?.(docId);
  }
  renderMapStorage();
  if (!gone.length) return;
  const titles = gone.map((docId) => mapEntry(docId)?.title || knownTitle(docId) || t("common.untitled"));
  const names = titles.map((title) => `“${title}”`).join(", ");
  const removed = await vaultDialog({
    title: t("app.mapStorage.goneTitle"),
    submit: t("app.mapStorage.goneRemove"),
    cancel: t("common.later"),
    content: `<p class="vault-dialog-message">${escapeHtml(t("app.mapStorage.goneMessage", { count: gone.length, names }))}</p>
      <p class="vault-dialog-message">${escapeHtml(t("app.mapStorage.goneUnsent"))}</p>
      <label class="vault-checkbox"><input type="checkbox" data-gone-backup> ${escapeHtml(t("app.mapStorage.goneBackup"))}</label>`,
    onSubmit: async (modal) => {
      if (!modal.querySelector("[data-gone-backup]")?.checked) return true;
      const { exportLocalCopy } = await import("./export-flow.js");
      for (let i = 0; i < gone.length; i++) {
        // A failure keeps the dialog open with the reason: nothing is removed.
        if (!(await exportLocalCopy(gone[i], titles[i]))) throw new Error(t("app.mapStorage.goneBackupNotSaved"));
      }
      return true;
    },
  });
  // Another map opens in its place (at start, it was never opened).
  const current = Store.get("currentDocId");
  if (!current || gone.includes(current)) await deps.closeMap?.(gone);
  if (!removed) {
    gone.forEach((docId) => goneAsked.delete(docId));
    return;
  }
  for (const docId of gone) await VaultStorage.forgetGoneMap(docId);
  renderMapStorage();
  showToast(t("app.mapStorage.goneRemoved", { count: gone.length, names }));
}

const openMapChecked = new Set();

/**
 * The open map left the vault (moved or deleted on another device): if this
 * device knew it online, ask the server (once; again if it didn't answer).
 */
async function checkOpenMapStillOnline() {
  const docId = Store.get("currentDocId");
  if (!docId || mapStorageOf(docId) !== null || !isKnownOnline(docId) || openMapChecked.has(docId)) return;
  openMapChecked.add(docId);
  const online = await stillOnline(docId);
  if (online === null) openMapChecked.delete(docId);
  if (online === false) await handleGoneMaps([docId]);
}

/** What the button shows and does for the open map. */
function describe(docId) {
  const storage = mapStorageOf(docId);
  if (!storage) return null;
  if (storage === "online") {
    // Document Properties offers the move to this device (desktop app, owner).
    const hint = canMoveToDevice(docId) ? t("app.mapStorage.onlineHintMovable") : t("app.mapStorage.onlineHint");
    return { storage, label: t("app.mapStorage.online"), hint, action: "properties" };
  }
  if (localOnlyMode()) {
    return { storage, label: t("app.mapStorage.device"), hint: t("app.mapStorage.deviceHintNoServer"), action: "properties" };
  }
  if (!deps.getCurrentUser?.()) {
    return { storage, label: t("app.mapStorage.device"), hint: t("app.mapStorage.deviceHintSignedOut"), action: "login" };
  }
  return { storage, label: t("app.mapStorage.device"), hint: t("app.mapStorage.deviceHint"), action: canMoveOnline() ? "move-online" : "properties" };
}

/** Redraw the button next to the map's title. */
export function renderMapStorage() {
  const btn = document.getElementById("map-storage-btn");
  if (!btn) return;
  const docId = Store.get("currentDocId");
  const view = describe(docId);
  btn.classList.toggle("hidden", !view);
  if (!view) return;
  // A map only this browser has, in a browser that may delete it.
  const atRisk = view.storage === "local" && knownClearRisk() === true;
  const hint = atRisk ? `${view.hint} ${t("vault.device.mayBeCleared")}` : view.hint;
  btn.dataset.storage = view.storage;
  btn.dataset.action = view.action;
  btn.toggleAttribute("data-at-risk", atRisk);
  btn.title = hint;
  btn.setAttribute("aria-label", `${t("app.mapStorage.label")}: ${view.label}. ${hint}`);
  btn.innerHTML = `${view.storage === "online" ? CLOUD : DEVICE}<span>${escapeHtml(view.label)}</span>`;
}

async function onClick() {
  const docId = Store.get("currentDocId");
  const view = describe(docId);
  if (!view) return;
  if (view.action === "move-online") await moveMapOnline(docId);
  else if (view.action === "login") deps.openLogin?.();
  else deps.openProperties?.();
}

/**
 * @param {{getCurrentUser: Function, reopenDoc: (docId: string) => Promise<void>,
 *   openProperties: Function, openLogin: Function,
 *   closeMap: (docIds: string[]) => Promise<void>}} options closeMap: opens
 *   another map than these (or none)
 */
export function initMapStorage(options) {
  deps = { ...deps, ...options };
  const btn = document.getElementById("map-storage-btn");
  if (btn && !btn.dataset.bound) {
    btn.dataset.bound = "1";
    btn.addEventListener("click", onClick);
  }
  on(Events.VAULT_INDEX_UPDATED, renderMapStorage);
  on(Events.MAPS_GONE_ONLINE, ({ docIds }) => { void handleGoneMaps(docIds); });
  on(Events.VAULT_INDEX_UPDATED, () => { void checkOpenMapStillOnline(); });
  on(Events.STORAGE_POLICY_CHANGED, renderMapStorage);
  on(Events.MAP_KEPT_ON_DEVICE, ({ title }) => {
    showToast(t("app.mapStorage.keptOnDevice", { title: title || t("common.untitled") }), { duration: 10000 });
  });
  void storageMayBeCleared();
  let lastDocId = Store.get("currentDocId");
  let lastUser = deps.getCurrentUser?.()?.user_id ?? null;
  Store.subscribe(() => {
    const docId = Store.get("currentDocId");
    const user = deps.getCurrentUser?.()?.user_id ?? null;
    if (docId === lastDocId && user === lastUser) return;
    const signedIn = user && user !== lastUser;
    lastDocId = docId;
    lastUser = user;
    renderMapStorage();
    if (signedIn) void offerUnfinishedMoves();
  });
  renderMapStorage();
  void offerUnfinishedMoves();
  // Once the account's vault has synced (at start, after signing in).
  on(Events.USER_CRDT_STATE_CHANGED, ({ state }) => {
    if (state === "synced") void offerUnfinishedMoves();
    else triedOnline.clear();
  });
  // The device is back online (its vault socket may not have noticed it was not).
  window.addEventListener("online", () => {
    triedOnline.clear();
    void offerUnfinishedMoves();
  });
  on(Events.VAULT_INDEX_UPDATED, () => { void offerUnfinishedMoves(); });
}
